'use strict';

import { Program } from '@playbook-technologies/common-enums';
import React from 'react';
import auth from '../auth';
import Faq from '../Faq';
import MediaEditor from '../MediaEditor';
import StatusBar from '../StatusBar';
import { CollectionType } from './CollectionType';
import PillSelect, { SquarePill } from './PillSelect';
import { COLLECTION_SETTINGS } from './collections.settings';
import TagEditor from '../TagEditor';
import { PublishingBehaviourSelect } from './ProgramForm';
import { CollectionProgram } from './collections.types';
import { Toggle } from '../common/inputs/Toggle';
import { ColorPicker } from '../common/inputs/ColorPicker';
import ProgramReviews from './ProgramReviews';
import { changeUrl } from '@client/helpers/routes';
import { TextField, Typography } from '@mui/material';
import { PrimaryButton } from '@client/new/components/button';
import { mixTrack } from '@services/mixpanel';

type CategoryEditProps = {
  collection: CollectionProgram;
  disabledCollectionType: boolean;
  validationTree: any;
  router: any;
  route: unknown;
  onChange: (p: CollectionProgram) => void;
  typeChanged: (t: number) => void;
  title?: string;
  params: {
    influencerId: string;
    programId: string;
  };
  save: () => void;
  creationMode?: boolean;
  message?: string;
};
type CategoryEditState = {
  isModified: boolean;
  isSaving: boolean;
  message: string;
};
export class CategoryForm extends React.Component<
  CategoryEditProps,
  CategoryEditState
> {
  constructor(props) {
    super(props);
    this.state = {
      message: 'Loading...',
      isModified: false,
      isSaving: false,
    };
    this.getInfluencerId = this.getInfluencerId.bind(this);
    this.componentWillMount = this.componentWillMount.bind(this);
    this.routerWillLeave = this.routerWillLeave.bind(this);
    this.merge = this.merge.bind(this);
    this.validate = this.validate.bind(this);
    this.mediaIntroChanged = this.mediaIntroChanged.bind(this);
    this.mediaCoverChanged = this.mediaCoverChanged.bind(this);
    this.nameChanged = this.nameChanged.bind(this);
    this.descriptionChanged = this.descriptionChanged.bind(this);
    this.tagsChanged = this.tagsChanged.bind(this);
    this.faqChanged = this.faqChanged.bind(this);
    this.workoutLimitChanged = this.workoutLimitChanged.bind(this);
    this.publishingBehaviourChanged =
      this.publishingBehaviourChanged.bind(this);
  }

  getInfluencerId(): number {
    let influencerId =
      this.props.params.influencerId ||
      auth.getUser().influencerId ||
      this.props.collection.influencerId;
    return parseInt(String(influencerId), 10);
  }
  componentWillMount() {
    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);
  }
  componentDidMount(): void {
    const hash = window.document.location.hash;
    if (hash) {
      const section = window.document.querySelector(hash);
      if (section) {
        window.setTimeout(() => {
          window.scrollTo({
            top: section.getBoundingClientRect().top,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }

  routerWillLeave() {
    if (this.state.isModified && !this.state.isSaving) {
      return 'There are pending changes to the Collection, are you sure you want to leave without saving?';
    }
  }
  publishingBehaviourChanged(value) {
    this.merge({
      publishingBehaviour: value,
    });
  }
  typeChanged = (type) => {
    this.merge({ type });
    this.props.typeChanged(type);

    mixTrack({
      type: 'Category Added',
      payload: {
        'Category Type': type,
        'Tag Location': 'Category',
      },
    });
  };
  merge(newData: Partial<CollectionProgram>) {
    const data = { ...this.props.collection, ...newData };
    this.props.onChange(data);
  }

  toggleSection = (name: 'faq' | 'testimonials') => {
    const currentValues = this.props.collection.hiddenSections || {};
    this.merge({
      hiddenSections: {
        ...this.props.collection.hiddenSections,
        [name]: !currentValues[name],
      },
    });

    if (name === 'faq') {
      mixTrack({
        type: 'FAQ Toggled',
        payload: {
          'Collection Type': 'Category',
        },
      });
    }

    if (name === 'testimonials') {
      mixTrack({
        type: 'Reviews Toggled',
        payload: {
          'Collection Type': 'Category',
        },
      });
    }
  };

  validate(attribute: keyof CollectionProgram) {
    let data = this.props.collection;
    // console.log(`[ProgramEdit] Validating '${attribute}'.`, data);

    if (attribute) {
      if (this.props.validationTree) {
        // console.log(`[ProgramEdit] Validating attribute '${attribute}'...`);
        if (
          this.props.validationTree &&
          this.props.validationTree[attribute] &&
          this.props.validationTree[attribute].length > 0
        ) {
          console.log(`[ProgramEdit] Attribute '${attribute}' is invalid.`);
          return 'invalid';
        }
        return null;
      } else {
        return null;
      }
    }
  }
  mediaIntroChanged(mediaIntro) {
    this.merge({ mediaIntro });

    mixTrack({
      type: 'Intro Video Uploaded',
      payload: {
        'Collection Type': 'Category',
      },
    });
  }
  mediaCoverChanged(mediaCover) {
    this.merge({ mediaCover });

    mixTrack({
      type: 'Cover Image Uploaded',
      payload: {
        'Collection Type': 'Category',
      },
    });
  }
  targetAreaChanged = (targetArea) => {
    this.merge({ targetArea });

    mixTrack({
      type: 'Target Area Selected',
      payload: {
        'Tag Location': 'Category',
        'Target Area Type': targetArea,
      },
    });
  };
  difficultyChanged = (values) => {
    this.merge({ difficulty: values[0] });

    mixTrack({
      type: 'Difficulty Selected',
      payload: {
        'Difficulty Type': values[0],
        'Tag Location': 'Category',
      },
    });
  };
  equipmentChanged = (requiredEquipment) => {
    this.merge({ requiredEquipment });

    mixTrack({
      type: 'Equipment Added',
      payload: {
        'Equipment Type': requiredEquipment,
        'Tag Location': 'Category',
      },
    });
  };
  nameChanged(ev) {
    this.merge({ name: ev.target.value });
  }
  descriptionChanged(ev) {
    this.merge({ description: ev.target.value });
  }
  workoutLimitChanged(ev) {
    const workoutLimit = ev.target.value ? parseInt(ev.target.value) : null;
    const update: Partial<CollectionProgram> = { workoutLimit };
    if (
      workoutLimit &&
      this.props.collection.publishingBehaviour !==
        Program.PublishingBehaviour.Top.value
    ) {
      update.publishingBehaviour = Program.PublishingBehaviour.Top.value;
    }
    this.merge(update);

    mixTrack({
      type: 'Limit Toggled',
      payload: {
        'Limit Number': workoutLimit,
      },
    });
  }
  tagsChanged(tags) {
    this.merge({ tags });
  }
  faqChanged(faq) {
    this.merge({ faq });

    mixTrack({
      type: 'FAQ Added',
      payload: {
        'Collection Type': 'Category',
      },
    });
  }
  locationChanged = (values) => {
    this.merge({ location: values[0] });

    mixTrack({
      type: 'Location Selected',
      payload: {
        'Location Type': values[0],
        'Tag Location': 'Category',
      },
    });
  };
  colorChanged = (hex) => {
    this.merge({ color: hex.slice(1) });

    mixTrack({
      type: 'Color Selected',
      payload: {
        'Collection Type': 'Category',
      },
    });
  };

  render() {
    if (!this.props.collection) {
      return <div className="programEdit">{this.props.message}</div>;
    }

    let color = '#6367EF';
    if (
      this.props.collection.color &&
      this.props.collection.color.length === 6
    ) {
      color = `#${this.props.collection.color}`;
    }

    const isSaving = this.state.isSaving;
    const save = this.props.save;

    const categoryName = this.props.collection.name;

    return (
      <div className="categoryEdit">
        <main id="main">
          <div key="video" className="flex flex-col gap-8">
            <section>
              <div className="flex flex-row gap-3 justify-end">
                <div
                  className={
                    'button-box' + (isSaving ? ' pointer-events-none' : '')
                  }
                >
                  <PrimaryButton
                    isLoading={isSaving}
                    onClick={() => save()}
                    data-testid="subscription-category-save"
                  >
                    {this.props.creationMode ? 'Create' : 'Save'}
                  </PrimaryButton>
                </div>
              </div>
            </section>

            <section>
              <div className="flex justify-between items-center pb-5">
                <div>
                  {this.props.disabledCollectionType ? null : (
                    <CollectionType
                      type={this.props.collection.type}
                      disabled={this.props.disabledCollectionType}
                      onChange={this.typeChanged}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <div>
                  <div className="heading-block">
                    <Typography>Intro video (optional)</Typography>
                  </div>
                  <div
                    className={`media-box square ${this.validate(
                      'mediaIntro',
                    )}`}
                  >
                    <MediaEditor
                      className="vertical"
                      metadata={this.props.collection?.mediaIntro}
                      onChange={this.mediaIntroChanged}
                      onRemove={this.mediaIntroChanged}
                      format="video"
                      accept="video/*"
                    />
                  </div>
                </div>
                <div>
                  <div className="heading-block">
                    <Typography>
                      Cover image <strong style={{ color: 'red' }}>*</strong>
                    </Typography>
                  </div>
                  <div
                    className={`media-box square ${this.validate(
                      'mediaCover',
                    )}`}
                  >
                    <MediaEditor
                      className="square"
                      metadata={this.props.collection.mediaCover}
                      onChange={this.mediaCoverChanged}
                      onRemove={this.mediaCoverChanged}
                      format="image"
                      accept="image/*"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="input-holder flex flex-col  max-w-xl">
                  <label>
                    Title <strong style={{ color: 'red' }}>*</strong>
                  </label>
                  <TextField
                    type="text"
                    onChange={this.nameChanged}
                    className={`${this.validate('name')}`}
                    value={this.props.collection.name}
                    inputProps={{ maxLength: 50 }}
                    data-testid="subscription-category-name-input"
                  />
                </div>
                <div className="input-holder max-w-xs">
                  <label>
                    Color <strong style={{ color: 'red' }}>*</strong>
                  </label>
                  <ColorPicker
                    value={'#' + this.props.collection.color}
                    onChange={this.colorChanged}
                    showColorBox
                  />
                </div>
                <div className="input-holder flex flex-col max-w-xl">
                  <label>Description (optional)</label>
                  <TextField
                    multiline
                    onChange={this.descriptionChanged}
                    value={this.props.collection.description}
                    inputProps={{ maxLength: 2000 }}
                  />
                </div>
              </div>

              <div className="input-holder">
                <div>
                  <Typography variant="h2">Details</Typography>
                </div>
                <div className="space-y-4">
                  <div>
                    <PillSelect
                      label="Category"
                      multi
                      limit={3}
                      values={this.props.collection.tags}
                      options={COLLECTION_SETTINGS.COLLECTION_TAGS.map((g) => ({
                        value: g,
                        name: g,
                      }))}
                      onChange={(tags) => this.merge({ tags })}
                    />
                  </div>
                  <div>
                    <PillSelect
                      invalid={this.validate('targetArea')}
                      label="Target Area"
                      multi
                      limit={2}
                      values={this.props.collection.targetArea}
                      options={COLLECTION_SETTINGS.COLLECTION_TARGET_AREA.map(
                        (g) => ({
                          value: g,
                          name: g,
                        }),
                      )}
                      onChange={this.targetAreaChanged}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-2">Equipment</label>
                    <TagEditor
                      onChange={this.equipmentChanged}
                      tags={this.props.collection.requiredEquipment || []}
                      placeholder="Type keyword here..."
                      maxLength={20}
                      maxItems={7}
                      allowSpaces={true}
                    />
                    <div className="mt-2">
                      <small>
                        Press Enter after typing to add new one <br />
                        Example: bands, kettlebell, mat, basketball
                      </small>
                    </div>
                  </div>

                  <PillSelect
                    label="Difficulty"
                    Pill={SquarePill}
                    values={this.props.collection.difficulty}
                    options={COLLECTION_SETTINGS.COLLECTION_DIFFICULTY}
                    onChange={this.difficultyChanged}
                    gap={0}
                  />
                  <PillSelect
                    label="Location"
                    Pill={SquarePill}
                    values={this.props.collection.location}
                    options={COLLECTION_SETTINGS.COLLECTION_LOCATION}
                    onChange={this.locationChanged}
                    gap={0}
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <Typography variant="h2">Settings</Typography>
              <div className="max-w-xl">
                <PublishingBehaviourSelect
                  publishingBehaviour={
                    this.props.collection.publishingBehaviour
                  }
                  invalid={this.validate('publishingBehaviour')}
                  onChange={this.publishingBehaviourChanged}
                />
                <div className="flex flex-col">
                  <label className="mb-2">Workout Limit (optional)</label>
                  <TextField
                    id="workout-limit"
                    className="reps"
                    style={{ width: '100px' }}
                    type="number"
                    // pattern="\d*"
                    value={this.props.collection.workoutLimit}
                    onChange={this.workoutLimitChanged}
                  />
                </div>
              </div>
            </section>

            <section className="relative">
              <Typography variant="h2">
                FAQ <span className="text-lg font-normal">(optional)</span>
              </Typography>
              <div className="absolute right-6 top-6">
                <Toggle
                  value={!this.props.collection.hiddenSections?.faq}
                  onChange={() => this.toggleSection('faq')}
                />
              </div>
              <Faq
                questions={this.props.collection.faq}
                onChange={this.faqChanged}
                tree={
                  this.props.validationTree && this.props.validationTree.faq
                    ? this.props.validationTree.faq
                    : null
                }
              />
            </section>

            <section className="relative">
              <div className="big-heading">
                <div
                  style={{
                    borderBottom: this.validate('testimonials')
                      ? '1px solid red'
                      : undefined,
                  }}
                >
                  <Typography variant="h2">
                    Reviews{' '}
                    <span className="text-lg font-normal">(optional)</span>
                  </Typography>
                </div>
              </div>
              <div className="absolute right-6 top-6">
                <Toggle
                  value={!this.props.collection.hiddenSections?.testimonials}
                  onChange={() => this.toggleSection('testimonials')}
                />
              </div>
              <div>
                <ProgramReviews
                  onChange={(testimonials) => this.merge({ testimonials })}
                  value={this.props.collection.testimonials || []}
                  errors={[]}
                />
              </div>
            </section>
          </div>
        </main>
        <StatusBar.Component />
      </div>
    );
  }
}
